import * as Yup from 'yup';
import { useState } from 'react';
import { Grid } from '@mui/material';
import { Formik } from 'formik';

import PageHeader from 'src/components/organisms/PageHeader';
import Footer from 'src/components/molecules/footer/Footer';
import ContentCardsContainer, { CardTab, CardType, ContentCard, HeaderTabs } from 'src/components/organisms/ContentCard';
import { FormContainerMolecule } from 'src/components/molecules/formik/FormContainerMolecule';
import { FormField } from 'src/components/molecules/formik/FormField';
import { CustomDataGrid } from 'src/components/organisms/CustomDataGrid';
import DetailPanelContainer from 'src/components/organisms/tables/DetailPanelContainer';

import useSubmit from 'src/hooks/forms/useSubmit';
import FormHelper from 'src/utils/formHelper';
import { buildSupplierRequest } from 'src/utils/submit/submitRequests';

import { useInvoiceForm } from 'src/hooks/forms/useForms';
import { useInvoiceRowDetailsTable, useInvoiceRowsTable } from 'src/hooks/table/configs/useInvoicesTable';

// ⬇️ Keep your request infra; we use useRequest directly
// ⬇️ Reuse your existing request builder (returns AxiosRequestConfig)
import PrintInvoiceButton from 'src/components/atoms/buttons/PrintInvoiceButton';

// --- Validation (lean for now; extend as needed) ---
const scheme = Yup.object().shape({
  SalesOrderId: Yup.number().nullable(),
  TaxIncluded: Yup.boolean().required(),
  TotalAmountGross: Yup.number().nullable(),
  TotalAmountNet: Yup.number().nullable(),
  TotalAmountNoG: Yup.number().nullable(),
  ShippingAddress: Yup.object().shape({
    FirstName: Yup.string().nullable(),
    LastName: Yup.string().nullable(),
    Street: Yup.string().nullable(),
    Postcode: Yup.string().nullable(),
    City: Yup.string().nullable(),
    Country: Yup.object().shape({ Key: Yup.number().nullable(), Value: Yup.string().nullable() }).nullable(),
    EmailAddress: Yup.string().email().nullable(),
    PhoneNumber: Yup.string().nullable(),
  })
});

const defaultInitialValues = {};

export const EditInvoice = () => {
  const [editId, PageName, currentSubmitFunction, , isEditPage] =
    FormHelper.getEditPageConfig('Invoice', buildSupplierRequest, buildSupplierRequest);

  const initialValues = useInvoiceForm(editId, defaultInitialValues);
  const [rowsColumns, rowsTableSettings] = useInvoiceRowsTable(initialValues);

  const [result, submit] = useSubmit(currentSubmitFunction);

 

  const tabs: CardTab[] = [
    { value: 'Invoice Rows', label: 'Invoice Rows' },
    { value: 'Shipping Address', label: 'Shipping Address' }
  ];
  const [currentTab, setCurrentTab] = useState<string>('Invoice Rows');

  return (
    <>
      <Formik initialValues={initialValues} validationSchema={scheme} onSubmit={submit} enableReinitialize>
        {(props): JSX.Element => (
          <div>
            <PageHeader
              {...{
                title: PageName,
                initialValues: initialValues,
                subTitle: 'View and Print Invoice',
                components: [
                  // { isInGroupedMenu: false, component: <CustomButton isSubmitting={props.isSubmitting} onClick={props.handleSubmit} title="Save" icon={<SaveIcon />} /> },
                  {
                    isInGroupedMenu: false,
                    component: (
                        <PrintInvoiceButton invoiceId={editId} />
                    )
                  },
                ],
                alertData: { dataArray: (result ?? false) ? [result] : null }
              }}
            />

            {/* --- Pre-tab content cards (metadata + totals) --- */}
            <ContentCardsContainer>
              <ContentCard {...{ cardId: 'Invoice Info', md: 6 }}>
                <FormContainerMolecule formikProps={props} disabled={true}>
                  <FormField fieldProps={{ name: 'SalesOrderId', label: 'Sales Order Id', md: 3.8 }} formikProps={props} />
                  <FormField fieldProps={{ name: 'CustomerAccountUserId', label: 'Customer Account User Id', md: 3.8 }} formikProps={props} />
                  <FormField fieldProps={{ name: 'SalesOrganizationId', label: 'Sales Organization Id', md: 3.8 }} formikProps={props} />

                  <FormField fieldProps={{ name: 'CustomerReference', label: 'Customer Reference', md: 5.8 }} formikProps={props} />
                  <FormField fieldProps={{ name: 'CustomerOrderReference', label: 'Customer Order Reference', md: 5.8 }} formikProps={props} />

                  <FormField fieldProps={{ name: 'ShippingMethod.Key', label: 'Shipping Method Key', md: 3.8 }} formikProps={props} />
                  <FormField fieldProps={{ name: 'ShippingMethod.Value', label: 'Shipping Method', md: 7.8 }} formikProps={props} />

                  <FormField fieldProps={{ name: 'FulfillDate', label: 'Fulfill Date', type: 'date', md: 5.8 }} formikProps={props} />
                  <FormField fieldProps={{ name: 'TaxIncluded', checkbox: true, label: 'Tax Included', md: 5.8 }} formikProps={props} />

                  <FormField fieldProps={{ name: 'Specification', label: 'Specification', multiline: true, rows: 3, md: 11.8 }} formikProps={props} />
                </FormContainerMolecule>
              </ContentCard>

              <ContentCard {...{ cardId: 'Totals', md: 6 }}>
                <FormContainerMolecule formikProps={props} disabled={true}>
                  <Grid container spacing={2}>
                    <FormField fieldProps={{ name: 'TotalAmountNoG', label: 'Total Amount (NoG)', md: 3.8, disabled: true }} formikProps={props} />
                    <FormField fieldProps={{ name: 'TotalAmountNet', label: 'Total Amount (Net)', md: 3.8, disabled: true }} formikProps={props} />
                    <FormField fieldProps={{ name: 'TotalAmountGross', label: 'Total Amount (Gross)', md: 3.8, disabled: true }} formikProps={props} />
                  </Grid>
                </FormContainerMolecule>
              </ContentCard>
            </ContentCardsContainer>

            {/* --- Tabs: Invoice Rows + Shipping Address --- */}
            <ContentCardsContainer>
              <ContentCard
                {...{
                  cardId: 'TabContainer',
                  contentPadding: 0,
                  cardTypes: [CardType.tabContainer],
                  cardHeader: <HeaderTabs {...{ tabs, currentTab, setCurrentTab }} />,
                }}
              >
                {currentTab === 'Invoice Rows' && (
                  <ContentCard {...{ cardId: 'Invoice Rows', cardTypes: [CardType.tab], contentPadding: 0 }}>
                    <CustomDataGrid
                      columns={rowsColumns}
                      tableSettings={rowsTableSettings}
                      getDetailPanelContent={(row) => <DetailPanelContent {...{ row }} />}
                    />
                  </ContentCard>
                )}

                {currentTab === 'Shipping Address' && (
                  <ContentCard {...{ cardId: 'Shipping Address', cardTypes: [CardType.tab] }}>
                    <FormContainerMolecule formikProps={props} disabled={true}>
                      <FormField fieldProps={{ name: 'ShippingAddress.Company', label: 'Company', md: 5.8 }} formikProps={props} />
                      <FormField fieldProps={{ name: 'ShippingAddress.Department', label: 'Department', md: 5.8 }} formikProps={props} />

                      <FormField fieldProps={{ name: 'ShippingAddress.FirstName', label: 'First Name', md: 3.8 }} formikProps={props} />
                      <FormField fieldProps={{ name: 'ShippingAddress.MiddleNameInitial', label: 'Middle Initial', md: 3.8 }} formikProps={props} />
                      <FormField fieldProps={{ name: 'ShippingAddress.LastName', label: 'Last Name', md: 3.8 }} formikProps={props} />

                      <FormField fieldProps={{ name: 'ShippingAddress.Street', label: 'Street', md: 5.8 }} formikProps={props} />
                      <FormField fieldProps={{ name: 'ShippingAddress.StreetAdditionalInformation', label: 'Street Additional Information', md: 5.8 }} formikProps={props} />

                      <FormField fieldProps={{ name: 'ShippingAddress.Postcode', label: 'Postcode', md: 3.8 }} formikProps={props} />
                      <FormField fieldProps={{ name: 'ShippingAddress.City', label: 'City', md: 3.8 }} formikProps={props} />
                      <FormField fieldProps={{ name: 'ShippingAddress.County', label: 'County', md: 3.8 }} formikProps={props} />

                      <FormField fieldProps={{ name: 'ShippingAddress.State', label: 'State', md: 3.8 }} formikProps={props} />

                      <FormField fieldProps={{ name: 'ShippingAddress.Country.Key', label: 'Country Key', md: 3.8 }} formikProps={props} />
                      <FormField fieldProps={{ name: 'ShippingAddress.Country.Value', label: 'Country', md: 3.8 }} formikProps={props} />

                      <FormField fieldProps={{ name: 'ShippingAddress.EmailAddress', label: 'Email Address', md: 5.8 }} formikProps={props} />
                      <FormField fieldProps={{ name: 'ShippingAddress.PhoneNumber', label: 'Phone Number', md: 5.8 }} formikProps={props} />

                      <FormField fieldProps={{ name: 'ShippingAddress.MobileNumber', label: 'Mobile Number', md: 5.8 }} formikProps={props} />
                      <FormField fieldProps={{ name: 'ShippingAddress.FaxNumber', label: 'Fax Number', md: 5.8 }} formikProps={props} />

                      <FormField fieldProps={{ name: 'ShippingAddress.DoorCode', label: 'Door Code', md: 3.8 }} formikProps={props} />
                      <FormField fieldProps={{ name: 'ShippingAddress.AdditionalInformation', label: 'Additional Information', md: 7.8 }} formikProps={props} />
                    </FormContainerMolecule>
                  </ContentCard>
                )}
              </ContentCard>
            </ContentCardsContainer>
          </div>
        )}
      </Formik>

      <Footer />
    </>
  );
};

export default EditInvoice;

// --- Detail panel for Invoice Rows (shows InvoiceRowDetails as a nested table) ---
const DetailPanelContent = ({ row }) => {
  const [columns, tableSettings] = useInvoiceRowDetailsTable(row?.row?.InvoiceRowDetails);
  return (
    <DetailPanelContainer depth={0}>
      <CustomDataGrid columns={columns} tableSettings={tableSettings} />
    </DetailPanelContainer>
  );
};
